import { UserListModal } from '@/components/UserListModal';
import { useParams } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WidgetTableProps } from '@/components/WidgetTable/types';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { useToolUsers } from '@/hooks/api/useShadowAiTools';

interface AIToolUsageUserListProps {
  toolName: string;
  toolId: string;
  onClose: () => void;
  open: boolean;
}

interface AIToolUser {
  userId: string;
  email: string | null;
  name: string | null;
  timestamp: string;
}

const COLUMNS: WidgetTableProps<AIToolUser>['columns'] = [
  {
    header: 'User',
    id: 'user',
    cell: user => (
      <div>
        <div className="font-medium">{user.name || 'Unknown User'}</div>
        {user.email && (
          <div className="text-sm text-muted-foreground">{user.email}</div>
        )}
      </div>
    ),
  },
  {
    header: 'Last Seen',
    id: 'lastSeen',
    cell: user => <RelativeTimeWithTooltip date={user.timestamp} />,
  },
];

export const AIToolUsageUserList = ({
  toolName,
  toolId,
  onClose,
  open,
}: AIToolUsageUserListProps) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const rawQuery = useToolUsers(organizationId, toolId, open);

  // Transform the query to extract just the users array
  // Similar to MCPUsageUserList pattern
  const query = {
    ...rawQuery,
    data: rawQuery.data?.users ?? [],
  } as unknown as UseQueryResult<AIToolUser[]>;

  return (
    <UserListModal<AIToolUser>
      open={open}
      title={
        <>
          Users with <span className="font-mono">{toolName}</span>
        </>
      }
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
      query={query}
      columns={COLUMNS}
    />
  );
};
